// Field help for the Usage tab's explorer (TASK-252, SCR-232): the drill-down from the org to a
// team to a user, its filters, and the share, change, price and quota columns; and the Token
// classes panel (SCR-584), the by-model Fell back column (SCR-586) and the Billing attribution
// card (SCR-585). Keys are the ids passed to
// <Field help="…"> and the columns' `help`.
import type { HelpTopic } from "../topic";

export const USAGE_EXPLORER_TOPICS = {
  "usage.explorer": {
    title: "Usage explorer",
    body: "The customer's usage one level at a time: its teams, then one team's users, then one user's models. Open a name to go a level down; the trail above the table goes back up. Filters and the ranking stay as you move.",
    example: "Open Platform to see its developers, then Ana to see the models she used; “All teams” returns to the top.",
    article: "users-teams-and-sign-in",
    section: "users-and-teams",
  },
  "usage.range": {
    title: "Range",
    body: "The UTC days the explorer covers, both included. Change compares this range with the same number of days just before it.",
    example: "1 Sep – 30 Sep covers 30 days and compares with 2 Aug – 31 Aug.",
    article: "what-nexus-does",
  },
  "usage.platform": {
    title: "Coding tool",
    body: "Limit the figures to requests from one coding tool, as the proxy read it from the client. Leave it empty for every tool.",
    example: "claude-code shows only Claude Code's requests; codex only Codex's.",
    article: "claude-code-and-codex",
  },
  "usage.price": {
    title: "Price",
    body: "The usage at the customer's own rate plan, priced cell by cell before the month's terms (volume discount, trial credit, minimum, fees). “—” means the figure is not shown for this month or a model has no price.",
    example: "$1,300.00 of AWS list cost at a 30 % markup is a price of $1,690.00.",
    article: "pricing-and-markup",
    section: "tokens-to-invoice",
  },
  "usage.share": {
    title: "Share",
    body: "This row's part of the level above, on the measure the rows are ranked by. “—” when that measure is money not shown here.",
    example: "Ranked by tokens, a team at 25.0 % used a quarter of the customer's tokens in the range.",
    article: "users-teams-and-sign-in",
    section: "users-and-teams",
  },
  "usage.delta": {
    title: "Change",
    body: "How the ranked measure moved against the previous period of the same length. “New” means nothing in the previous period; “—” means nothing in either, or a figure not shown.",
    example: "+12.5% on tokens: 900k in the previous 30 days, 1.01M in this range.",
    article: "users-teams-and-sign-in",
    section: "users-and-teams",
  },
  "usage.standing": {
    title: "Quota",
    body: "The quota of this team or user nearest its limit: its state and how much of it is used. Hover it for the quota's name, measure, period and reset. “—” when no quota applies.",
    example: "Near limit · 83% means 83 % of the tightest quota is used this period; Exhausted means requests are refused until it resets.",
    article: "users-teams-and-sign-in",
    section: "users-and-teams",
  },
  "usage.grain": {
    title: "Chart period",
    body: "How the usage charts bucket time: by day over the last 30 days, by ISO week (Monday to Sunday) over 13 weeks, by month over 13 months, or by year over three years. The table and the share pie cover the same span.",
    example: "Monthly shows one point per month, Mar 2026 to Mar 2027, each the whole month's tokens.",
    article: "what-nexus-does",
  },
  "usage.classes": {
    title: "Token classes",
    body: "Tokens and money split by kind: input, output, cache read, and cache write at the 5-minute and the 1-hour lifetime. The 1-hour write is part of the cache-write tokens, never on top of them, so the five rows add up to the total. Money columns appear only where the figure is shown to this viewer.",
    example: "2.0M cache-write tokens of which 0.5M are 1-hour: Cache write (5 min) shows 1.5M and Cache write (1 h) 0.5M, and the total counts 2.0M once.",
    article: "pricing-and-markup",
    section: "tokens-to-invoice",
  },
  "usage.fallbacks": {
    title: "Fell back",
    body: "Requests this model answered because the model the client asked for had no capacity on AWS, and the customer's fallbacks named this one. Only the answered request counts; the attempt that failed is neither a request nor an error. “—” means none.",
    example: "Sonnet 5 shows 38 with “38 requests fell back from Opus 5.5”: Opus 5.5 was throttled 38 times and Sonnet 5 answered and was billed.",
    article: "claude-code-and-codex",
    section: "configuring-models",
  },
  "usage.attribution": {
    title: "Billing attribution",
    body: "How the customer's Bedrock calls of the last 24 hours were billed in their AWS account: to the person who made each call, or to the proxy's own role and why. The share leaves out calls by service credentials, from installations with attribution off, and from proxies too old to report it. Nexus bills every call the same either way.",
    example: "99.6 % with one installation at 40 % under the proxy's role, reason “AWS refused to start a person's session”: that installation's Bedrock role must allow sts:TagSession for the proxy.",
    article: "installations",
    section: "billing-attribution-in-aws-billing",
  },
} satisfies Record<string, HelpTopic>;
